import { useCallback, useMemo } from "react";
import { useTextHighlighting } from "../../../providers/TextHighlightingProvider";
import { HighlightMatch, TextSegment, HighlightResult } from "../types";
import { highlightedItemsTracker } from "@/lib/services/HighlightedItemsTracker";
import { highlightVisibilityService } from "@/lib/services/HighlightVisibilityService";
import { manualHighlightExclusionsService } from "@/lib/services/ManualHighlightExclusionsService";

export const useTextHighlightManager = () => {
  const { highlightItems } = useTextHighlighting();

  // Filter out hidden items - only apply highlights for visible ones
  const visibleHighlightItems = useMemo(() => {
    return highlightItems.filter(item => 
      highlightVisibilityService.isVisible(item.id, item.type)
    );
  }, [highlightItems]);

  // Escape regex special characters
  const escapeRegExp = useCallback((string: string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }, []);

  // Strip existing highlights from content to get clean text
  const stripExistingHighlights = useCallback((html: string): string => {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;
    
    // Remove all highlight spans but keep their text content
    const highlights = tempElement.querySelectorAll('.text-highlight');
    highlights.forEach(highlight => {
      const textContent = highlight.textContent || '';
      const textNode = document.createTextNode(textContent);
      highlight.parentNode?.replaceChild(textNode, highlight);
    });
    
    return tempElement.innerHTML;
  }, []);

  // Clean up invalid highlights (e.g., when text is deleted or items are hidden)
  // If extra text was typed after a highlighted word, split it to keep the highlight on the valid part
  const cleanupInvalidHighlights = useCallback((element: HTMLDivElement): boolean => {
    const highlightSpans = element.querySelectorAll('.text-highlight');
    let hasChanges = false;
    
    // Save cursor position info before any DOM changes
    const selection = window.getSelection();
    let cursorInfo: { span: Element; offset: number } | null = null;
    
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const cursorNode = range.startContainer;
      const cursorOffset = range.startOffset;
      
      // Check if cursor is inside any highlight span
      highlightSpans.forEach(span => {
        if (span.contains(cursorNode)) {
          // Calculate offset relative to the span's text content
          let offset = cursorOffset;
          if (cursorNode !== span) {
            // Cursor is in a text node inside the span
            // Calculate total offset from start of span
            const walker = document.createTreeWalker(span, NodeFilter.SHOW_TEXT, null);
            let node;
            let totalOffset = 0;
            while ((node = walker.nextNode()) !== null) {
              if (node === cursorNode) {
                offset = totalOffset + cursorOffset;
                break;
              }
              totalOffset += node.textContent?.length || 0;
            }
          }
          cursorInfo = { span, offset };
        }
      });
    }
    
    let newCursorNode: Text | null = null;
    let newCursorOffset = 0;
    
    highlightSpans.forEach(span => {
      const textContent = span.textContent || '';
      const highlightId = span.getAttribute('data-highlight-id');
      const highlightType = span.getAttribute('data-highlight-type') || '';
      const highlightName = span.getAttribute('data-highlight-name') || '';
      
      // Remove highlight if the item is now hidden
      if (highlightId && !highlightVisibilityService.isVisible(highlightId, highlightType)) {
        hasChanges = true;
        const textNode = document.createTextNode(textContent);
        
        // Track if this is the span with the cursor
        if (cursorInfo && cursorInfo.span === span) {
          newCursorNode = textNode;
          newCursorOffset = Math.min(cursorInfo.offset, textContent.length);
        }
        
        span.parentNode?.replaceChild(textNode, span);
        return;
      }
      
      // Find the highlight item
      const highlightItem = highlightItems.find(item => item.id === highlightId);
      
      if (highlightItem) {
        // Check if the current text EXACTLY matches any of the highlight keys (case-insensitive)
        const trimmedText = textContent.trim();
        const isExactMatch = highlightItem.keys.some(key => 
          key.toLowerCase() === trimmedText.toLowerCase()
        );
        
        if (!isExactMatch) {
          // Check if any key is a prefix of the text (user typed more after the highlighted word)
          // Find the longest matching key that starts the text
          let matchingKey: string | null = null;
          let matchEndIndex = 0;
          
          for (const key of highlightItem.keys) {
            const keyLower = key.toLowerCase();
            const textLower = textContent.toLowerCase();
            
            // Check if text starts with the key followed by a space or non-word character
            if (textLower.startsWith(keyLower)) {
              const charAfterKey = textContent[key.length];
              // Key must be followed by space, punctuation, or end of string
              if (charAfterKey === undefined || /[\s\p{P}]/u.test(charAfterKey)) {
                if (key.length > matchEndIndex) {
                  matchingKey = key;
                  matchEndIndex = key.length;
                }
              }
            }
            
            // Also check if text ends with the key preceded by a space (user typed before)
            if (textLower.endsWith(keyLower)) {
              const charBeforeKey = textContent[textContent.length - key.length - 1];
              if (charBeforeKey === undefined || /[\s\p{P}]/u.test(charBeforeKey)) {
                // Text typed before the highlight - we'll handle this case by unwrapping
                // and letting auto-highlight reapply on blur
              }
            }
          }
          
          if (matchingKey && matchEndIndex < textContent.length) {
            // Split: keep highlight on the matching part, put the rest as plain text
            hasChanges = true;
            
            const highlightedPart = textContent.substring(0, matchEndIndex);
            const plainPart = textContent.substring(matchEndIndex);
            
            // Create new highlight span for the matching part
            const newHighlightSpan = document.createElement('span');
            newHighlightSpan.className = 'text-highlight';
            newHighlightSpan.setAttribute('data-highlight-id', highlightId || '');
            newHighlightSpan.setAttribute('data-highlight-type', highlightType);
            if (highlightName) {
              newHighlightSpan.setAttribute('data-highlight-name', highlightName);
            }
            newHighlightSpan.textContent = highlightedPart;
            
            // Create text node for the plain part
            const plainTextNode = document.createTextNode(plainPart);
            
            // Replace the original span with the new structure
            const parent = span.parentNode;
            if (parent) {
              parent.insertBefore(newHighlightSpan, span);
              parent.insertBefore(plainTextNode, span);
              parent.removeChild(span);
              
              // Track cursor position - it's likely in the plain part (after the highlighted word)
              if (cursorInfo && cursorInfo.span === span) {
                if (cursorInfo.offset <= matchEndIndex) {
                  // Cursor is in the highlighted part - this shouldn't normally happen
                  // but handle it anyway
                  newCursorNode = newHighlightSpan.firstChild as Text;
                  newCursorOffset = cursorInfo.offset;
                } else {
                  // Cursor is in the plain part (most common case when typing after highlight)
                  newCursorNode = plainTextNode;
                  newCursorOffset = cursorInfo.offset - matchEndIndex;
                }
              }
            }
          } else {
            // No matching prefix found - unwrap the entire highlight
            hasChanges = true;
            const textNode = document.createTextNode(textContent);
            
            // Track if this is the span with the cursor
            if (cursorInfo && cursorInfo.span === span) {
              newCursorNode = textNode;
              newCursorOffset = Math.min(cursorInfo.offset, textContent.length);
            }
            
            span.parentNode?.replaceChild(textNode, span);
          }
        }
      }
    });
    
    // Restore cursor position if it was inside a replaced span
    if (newCursorNode && selection) {
      try {
        const range = document.createRange();
        range.setStart(newCursorNode, newCursorOffset);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      } catch (e) {
        // Ignore cursor restoration errors
      }
    }
    
    return hasChanges;
  }, [highlightItems]);

  // Clean up highlighted HTML to ensure proper structure
  const cleanupHighlightedHTML = useCallback((html: string): string => {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;
    
    // Remove any nested highlight spans (avoid double-wrapping)
    const nestedHighlights = tempElement.querySelectorAll('.text-highlight .text-highlight');
    nestedHighlights.forEach(nested => {
      const parent = nested.parentElement;
      if (parent && parent.classList.contains('text-highlight')) {
        // Move the content up to the parent level
        while (nested.firstChild) {
          parent.insertBefore(nested.firstChild, nested);
        }
        parent.removeChild(nested);
      }
    });
    
    // Ensure all highlight spans are properly closed
    const highlights = tempElement.querySelectorAll('.text-highlight');
    highlights.forEach(highlight => {
      // Ensure the span has proper attributes
      if (!highlight.getAttribute('data-highlight-id')) {
        highlight.setAttribute('data-highlight-id', 'unknown');
      }
      if (!highlight.getAttribute('data-highlight-type')) {
        highlight.setAttribute('data-highlight-type', 'unknown');
      }
    });
    
    // Clean up empty list items that might have been created during processing
    const allLists = tempElement.querySelectorAll('ul, ol');
    allLists.forEach(list => {
      const items = Array.from(list.querySelectorAll('li'));
      items.forEach(item => {
        // Remove completely empty list items (but preserve ones with just a br or placeholder)
        if (!item.textContent?.trim() && !item.querySelector('img, br, span.text-highlight')) {
          item.remove();
        } else if (item.textContent?.trim()) {
          // Ensure list items have proper text nodes
          // If a list item only has highlight spans and no text nodes between them,
          // the browser might render it incorrectly
          const hasOnlyHighlights = Array.from(item.childNodes).every(
            node => node.nodeType === Node.ELEMENT_NODE && 
                   (node as Element).classList?.contains('text-highlight')
          );
          
          // This is fine - list items can have only highlights
          // We just need to make sure the structure is preserved
          if (hasOnlyHighlights && item.childNodes.length === 0) {
            // This shouldn't happen, but if it does, restore some text
            item.textContent = item.textContent || '';
          }
        }
      });
      
      // If the list is now empty, remove it
      if (list.children.length === 0) {
        list.remove();
      }
    });
    
    return tempElement.innerHTML;
  }, []);

  // Process text with highlights while preserving existing HTML formatting
  const processTextWithHighlights = useCallback((element: HTMLDivElement, blockId?: string): HighlightResult => {
    const existingHTML = element.innerHTML;
    const highlights: HighlightMatch[] = [];
    
    // Create a temporary div to work with the existing HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = existingHTML;
    
    // Find all text nodes and apply highlights while preserving existing formatting
    const walker = document.createTreeWalker(
      tempDiv,
      NodeFilter.SHOW_TEXT,
      null
    );
    
    const textNodes: Text[] = [];
    let node;
    while ((node = walker.nextNode()) !== null) {
      textNodes.push(node as Text);
    }
    
    // Track position (occurrence index) for each item across the entire block
    const itemPositionCounters = new Map<string, number>();
    
    // Process each text node for highlights
    textNodes.forEach(textNode => {
      // Verify the text node is still in the DOM and hasn't been orphaned
      if (!textNode.parentNode || !tempDiv.contains(textNode)) {
        return;
      }
      
      const textContent = textNode.textContent || '';
      if (!textContent.trim()) return;
      
      // Find matches for this text node
      const nodeHighlights: HighlightMatch[] = [];

      // Identify {{ ... }} variable reference ranges in this text node
      // so we can skip highlight matches that fall inside them
      const varRefRanges: { start: number; end: number }[] = [];
      const varRefRangeRegex = /\{\{[^{}]*\}\}/g;
      let vrMatch;
      while ((vrMatch = varRefRangeRegex.exec(textContent)) !== null) {
        varRefRanges.push({ start: vrMatch.index, end: vrMatch.index + vrMatch[0].length });
      }
      
      // Use only visible items for highlighting
      visibleHighlightItems.forEach(item => {
        item.keys.forEach((key: string) => {
          const regex = new RegExp(`\\b${escapeRegExp(key)}\\b`, 'gi');
          let match;
          
          while ((match = regex.exec(textContent)) !== null) {
            // Get current position counter for this item
            const itemKey = `${item.id}|${match[0]}`;
            const position = itemPositionCounters.get(itemKey) || 0;
            
            const newHighlight = {
              start: match.index,
              end: match.index + match[0].length,
              item
            };
            
            // Check for overlaps with existing highlights
            const hasOverlap = nodeHighlights.some(existing => 
              (newHighlight.start < existing.end && newHighlight.end > existing.start)
            );

            // Check if the match falls inside a {{ }} variable reference token
            const insideVarRef = varRefRanges.some(vr =>
              newHighlight.start >= vr.start && newHighlight.end <= vr.end
            );
            
            // Check if this instance is excluded (if blockId is provided)
            const isExcluded = blockId ? 
              manualHighlightExclusionsService.isExcluded(blockId, match[0], item.id, position) : 
              false;
            
            // Only add if there's no overlap, not inside a var ref, and not excluded
            if (!hasOverlap && !insideVarRef && !isExcluded) {
              nodeHighlights.push(newHighlight);
            }
            
            // Increment position counter for this item (whether added or not)
            itemPositionCounters.set(itemKey, position + 1);
          }
        });
      });
      
      // Apply highlights to this text node while preserving parent formatting
      if (nodeHighlights.length > 0) {
        const parentNode = textNode.parentNode;
        
        // Safety check: ensure parent node exists
        if (!parentNode) return;
        
        // If the text node is already inside a highlight span, don't double-wrap
        if ((parentNode as Element).classList?.contains('text-highlight')) {
          return;
        }
        
        // Skip text nodes inside variable reference decoration spans —
        // these show resolved values and should not be highlighted
        if ((parentNode as Element).classList?.contains('var-ref')) {
          return;
        }
        
        // Build segments of text with highlights
        const segments: TextSegment[] = [];
        let lastEnd = textContent.length;
        
        // Process from right to left to avoid offset issues
        const sortedHighlights = [...nodeHighlights].sort((a, b) => b.start - a.start);
        
        for (const highlight of sortedHighlights) {
          // Add text after this highlight (if any)
          if (lastEnd > highlight.end) {
            segments.unshift({
              type: 'text',
              content: textContent.substring(highlight.end, lastEnd)
            });
          }
          
          // Add the highlight
          segments.unshift({
            type: 'highlight',
            content: textContent.substring(highlight.start, highlight.end),
            highlight: highlight
          });
          
          lastEnd = highlight.start;
        }
        
        // Add remaining text at the beginning (if any)
        if (lastEnd > 0) {
          segments.unshift({
            type: 'text',
            content: textContent.substring(0, lastEnd)
          });
        }
        
        // Replace the text node with the new structure
        try {
          // Create a document fragment to hold all new nodes
          const fragment = document.createDocumentFragment();
          
          segments.forEach((segment, index) => {
            if (segment.type === 'text') {
              // Always create text nodes, even for empty strings
              // This ensures cursor can be positioned between adjacent highlights
              const newTextNode = document.createTextNode(segment.content || '');
              fragment.appendChild(newTextNode);
            } else if (segment.type === 'highlight') {
              const highlightSpan = document.createElement('span');
              highlightSpan.className = 'text-highlight';
              highlightSpan.setAttribute('data-highlight-id', segment.highlight!.item.id);
              highlightSpan.setAttribute('data-highlight-type', segment.highlight!.item.type);
              if (segment.highlight!.item.name) {
                highlightSpan.setAttribute('data-highlight-name', segment.highlight!.item.name);
              }
              highlightSpan.textContent = segment.content;
              fragment.appendChild(highlightSpan);
              
              // If the next segment is also a highlight (no text between them),
              // insert a zero-width space to allow cursor positioning
              const nextSegment = segments[index + 1];
              if (nextSegment && nextSegment.type === 'highlight') {
                const spacer = document.createTextNode('\u200B');
                fragment.appendChild(spacer);
              }
            }
          });
          
          // Only replace if the fragment has content
          if (fragment.childNodes.length > 0) {
            // Replace the original text node with the fragment in one operation
            // This is more atomic and less likely to corrupt the DOM structure
            parentNode.replaceChild(fragment, textNode);
          }
        } catch (error) {
          console.error('Error replacing text node with highlighted content:', error);
          // If replacement fails, leave the original text node intact
          return;
        }
        
        // Add to global highlights array
        highlights.push(...nodeHighlights);
      }
    });
    
    // Clean up any malformed HTML that might cause styling bleed
    const cleanedHTML = cleanupHighlightedHTML(tempDiv.innerHTML);
    
    // NOTE: We don't track items here anymore - let page-level useContentHighlighting handle it
    // This prevents TextBlock from clearing the tracker when processing already-highlighted HTML
    
    return {
      originalText: existingHTML,
      highlightedHTML: cleanedHTML,
      highlights
    };
  }, [visibleHighlightItems, escapeRegExp, cleanupHighlightedHTML]);

  // Apply manual highlight to selected text
  const applyManualHighlight = useCallback((highlightData: any, selectedText: string): void => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      
      if (selectedText) {
        // Create highlighted span
        const span = document.createElement('span');
        span.className = 'text-highlight';
        span.setAttribute('data-highlight-id', highlightData.id);
        span.setAttribute('data-highlight-type', highlightData.type);
        if (highlightData.name) {
          span.setAttribute('data-highlight-name', highlightData.name);
        }
        span.textContent = selectedText;
        
        // Replace selection with highlighted span
        range.deleteContents();
        range.insertNode(span);
      }
    }
  }, []);

  // Remove highlight from selected text
  const removeHighlight = useCallback((selectedText: string): void => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      
      if (selectedText) {
        // Find parent highlight span and unwrap it
        let node = range.startContainer;
        while (node && node.nodeType !== Node.ELEMENT_NODE) {
          node = node.parentNode;
        }
        
        if (node && (node as Element).classList.contains('text-highlight')) {
          // Unwrap the highlight span
          const parent = node.parentNode;
          if (parent) {
            parent.replaceChild(document.createTextNode(selectedText), node);
          }
        }
      }
    }
  }, []);

  // Check if content needs highlighting
  const shouldApplyHighlights = useCallback((element: HTMLDivElement): boolean => {
    if (visibleHighlightItems.length === 0) return false;
    
    const textContent = element.textContent || '';
    return visibleHighlightItems.some(item =>
      item.keys.some(key => {
        const regex = new RegExp(`\\b${escapeRegExp(key)}\\b`, 'gi');
        return regex.test(textContent);
      })
    );
  }, [visibleHighlightItems, escapeRegExp]);

  return {
    stripExistingHighlights,
    cleanupInvalidHighlights,
    processTextWithHighlights,
    applyManualHighlight,
    removeHighlight,
    shouldApplyHighlights,
    highlightItems: visibleHighlightItems
  };
};
